import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";

/**
 * Generic list/CRUD manager for simple entity pages.
 *
 * props:
 *  title, description, endpoint, fields, primaryField, columns, extraFilters
 */
export default function EntityManager({
  title, description, endpoint, fields, primaryField = "full_name", columns,
  testidPrefix, onSaved,
}) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null = not open, {} = new, {...} = edit
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(endpoint, { params: search ? { search } : {} });
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, endpoint]);

  function openNew() {
    const initial = {};
    fields.forEach((f) => {
      initial[f.name] = f.default ?? (f.type === "checkbox" ? true : "");
    });
    setForm(initial);
    setEditing({});
  }

  function openEdit(item) {
    setForm({ ...item });
    setEditing(item);
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...form };
      // Clean empty strings to null for optional fields
      fields.forEach((f) => {
        if (f.type !== "checkbox" && payload[f.name] === "") payload[f.name] = null;
      });
      let saved;
      if (editing.id) {
        saved = await api.patch(`${endpoint}/${editing.id}`, payload);
        toast.success("Atualizado");
      } else {
        saved = await api.post(endpoint, payload);
        toast.success("Criado");
      }
      onSaved?.(saved?.data);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  async function remove(item) {
    if (!(await confirm({ description: `Excluir "${item[primaryField] || item.name || item.id}"?` }))) return;
    try {
      await api.delete(`${endpoint}/${item.id}`);
      toast.success("Excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  return (
    <div className="space-y-5" data-testid={`${testidPrefix}-page`}>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
          {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        </div>
        <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-700 hover:bg-blue-800" onClick={openNew} data-testid={`${testidPrefix}-btn-new`}>
              <Plus className="h-4 w-4 mr-2" /> Novo
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" data-testid={`${testidPrefix}-dialog`}>
            <DialogHeader>
              <DialogTitle>{editing?.id ? "Editar" : "Novo"} — {title}</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2 overflow-y-auto flex-1 min-h-0 pr-1" data-testid={`${testidPrefix}-form-body`}>
              {fields.map((f) => (
                <div key={f.name} className={`space-y-1.5 ${f.fullWidth ? "md:col-span-2" : ""}`}>
                  <Label htmlFor={f.name}>{f.label}{f.required ? " *" : ""}</Label>
                  {f.type === "textarea" ? (
                    <Textarea
                      id={f.name} rows={3}
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`${testidPrefix}-input-${f.name}`}
                    />
                  ) : f.type === "checkbox" ? (
                    <div className="flex items-center gap-2 h-10">
                      <Checkbox id={f.name} checked={!!form[f.name]}
                        onCheckedChange={(v) => setForm({ ...form, [f.name]: !!v })}
                        data-testid={`${testidPrefix}-chk-${f.name}`} />
                      <span className="text-sm text-slate-600">{f.checkboxLabel || f.label}</span>
                    </div>
                  ) : f.type === "select" ? (
                    <select
                      id={f.name} className="w-full border border-slate-300 rounded-md px-3 h-11 md:h-9 text-base md:text-sm bg-white"
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`${testidPrefix}-select-${f.name}`}
                    >
                      <option value="">—</option>
                      {(f.options || []).map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={f.name} type={f.type || "text"}
                      value={form[f.name] ?? ""}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                      data-testid={`${testidPrefix}-input-${f.name}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
              <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid={`${testidPrefix}-btn-save`}>
                {saving ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            className="pl-9" placeholder="Buscar..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid={`${testidPrefix}-search`}
          />
        </div>
      </div>

      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className="text-left font-semibold text-xs uppercase tracking-wider text-slate-600 px-4 py-3">
                    {c.label}
                  </th>
                ))}
                <th className="w-24 px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={columns.length + 1} className="text-center p-6 text-slate-400">Carregando...</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center p-8 text-slate-400">Nenhum registro encontrado.</td></tr>}
              {items.map((item, idx) => (
                <tr key={item.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`${testidPrefix}-row-${idx}`}>
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 text-slate-800">
                      {c.render ? c.render(item) : (item[c.key] ?? "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(item)} data-testid={`${testidPrefix}-btn-edit-${idx}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => remove(item)} data-testid={`${testidPrefix}-btn-del-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
